import React, { useMemo, useState } from 'react';
import './Showroom.css';

// ── THE SHOWROOM (Braeden 9/3) ────────────────────────────────────────────
//
//   "this is how i want people to look at cars, give it this exact design"
//   — with a picture: a curved glass screen, buyer filters down the left,
//   tall car cards running off the right edge.
//
// So a car search does not render as a grid of listing tiles any more. It
// renders as a screen you stand in front of. Everything on it is real: the
// cards are the ones she actually harvested off the sellers' own pages, and
// the filters on the left move THOSE cards — a control that cannot change
// anything is never drawn (see `groups` below, which are built from the data
// that came back, not from a fixed list of car facts).
//
// One card, one car, one click to the seller's listing. The chrome is dark
// because the reference is dark; day mode gets its own light-lab palette
// rather than the dark one washed out (house rule: both themes, always).

const YEAR_RX = /^(19|20)\d{2}$/;

// Two-word marques, so "Mercedes-Benz EQE 350 SUV" keeps the marque in the
// eyebrow and puts EQE 350 SUV in lights, and "Land Rover Defender" does not
// become the "Rover Defender".
const TWO_WORD = new Set(['land rover', 'alfa romeo', 'aston martin', 'rolls royce', 'rolls-royce', 'general motors']);

// Sellers do not agree on what a title is. TrueCar publishes "2023
// Mercedes-Benz EQE 350 SUV"; iseecars publishes "EQE EQE 350 4MAT - 19,391
// mi" — the model twice and the mileage glued on the end, which then read as
// part of the car's name in 40px type and repeated a number already sitting
// in its own box below. Measured on a live search 9/3.
const TRAILING_MILES = /\s*[-–—|·]\s*[\d,.]+\s*(?:mi|miles|km)\b\.?\s*$/i;
const TRAILING_PRICE = /\s*[-–—|·]\s*\$[\d,.]+\s*$/;
const DOUBLED_WORD = /\b([\w-]+)(\s+\1)\b/gi;

function cleanTitle(raw) {
  return String(raw || '')
    .replace(/\s+/g, ' ')
    .replace(TRAILING_MILES, '')   // "... - 19,391 mi"
    .replace(TRAILING_PRICE, '')   // "... - $31,080"
    .replace(DOUBLED_WORD, '$1')   // "EQE EQE 350" -> "EQE 350"
    .trim();
}

/** Split "2023 Mercedes-Benz EQE 350 SUV" into eyebrow + the name in lights. */
function nameParts(title) {
  const raw = cleanTitle(title);
  if (!raw) return { eyebrow: '', name: '' };
  const words = raw.split(' ');
  let i = 0;
  let year = '';
  if (YEAR_RX.test(words[0])) { year = words[0]; i = 1; }
  let brand = '';
  const pair = `${(words[i] || '').toLowerCase()} ${(words[i + 1] || '').toLowerCase()}`;
  if (TWO_WORD.has(pair)) { brand = `${words[i]} ${words[i + 1]}`; i += 2; }
  else if (words[i]) { brand = words[i]; i += 1; }
  const name = words.slice(i).join(' ');
  // A title with nothing left after the marque (a bare "Rivian R1S" is fine,
  // a bare "Rivian" is not) keeps the marque as the name rather than
  // shouting a year at an empty card.
  if (!name) return { eyebrow: year, name: brand || raw };
  return { eyebrow: [year, brand].filter(Boolean).join(' '), name };
}

// ── A COLOUR IS A COLOUR, NOT A MARKETING NAME (9/3) ─────────────────────
// Sellers publish "Velocity Blue Metallic", "Summit White", "Stellar Black
// Metallic". Two costs, both measured on a live search: the spec box showed
// "Velocity Bl…", and the colour filter listed nine one-car options instead
// of the four real ones. Both fixed by reading the base colour out of the
// name and keeping the seller's own words nowhere but the listing.
const BASE_COLOURS = ['white', 'black', 'silver', 'gray', 'grey', 'blue', 'red', 'green',
  'brown', 'beige', 'tan', 'gold', 'orange', 'yellow', 'purple', 'maroon', 'bronze'];
function baseColour(raw) {
  const s = String(raw || '').toLowerCase();
  if (!s) return '';
  const hit = BASE_COLOURS.find((c) => s.includes(c));
  if (!hit) return String(raw).split(/\s+/)[0];      // unknown: one word, not five
  return hit === 'grey' ? 'Gray' : hit.charAt(0).toUpperCase() + hit.slice(1);
}

// A seller who published nothing published nothing. "Unknown" in a spec box
// is worse than an empty one: it ellipsed to "Unknow…" and it made a filter
// option out of ignorance (measured 9/3, live search).
const JUNK = /^(unknown|unspecified|other|none|n\/?a|-{1,2})$/i;
const isReal = (s) => !!(s && s.value && !JUNK.test(String(s.value).trim()));

const specValue = (c, label) => {
  const s = (c.specs || []).find((x) => x && x.label === label);
  return s && isReal(s) ? String(s.value) : '';
};
const milesOf = (c) => {
  const v = specValue(c, 'miles').replace(/[^0-9]/g, '');
  return v ? Number(v) : null;
};
const fuelOf = (c) => {
  const f = specValue(c, 'fuel');
  if (f) return f;
  // TrueCar publishes the fuel in the description line, not the spec row.
  const d = String(c.desc || '');
  const m = d.match(/\b(Electric|Hybrid|Gasoline|Diesel|Plug-?in[^·]*)\b/i);
  return m ? m[1] : '';
};

const money = (n) => (n == null ? '' : '$' + Math.round(Number(n)).toLocaleString('en-US'));

// 39,422 -> 39.4k. Measured 9/3: the spec box is 60px wide and "39,422"
// ellipsed to "39,42…", which is not a number any more. Five figures of
// mileage is the normal case for a used car, so the box has to hold it.
function boxValue(s) {
  if (s.label === 'color') return baseColour(s.value);
  if (s.label !== 'miles') return s.value;
  const n = Number(String(s.value).replace(/[^0-9]/g, ''));
  if (!n) return s.value;
  if (n < 10000) return n.toLocaleString('en-US');
  const k = n / 1000;
  return (k >= 100 ? Math.round(k) : Math.round(k * 10) / 10) + 'k';
}

/** One filter group, built only when the cards actually differ on it. */
function optionsFor(cards, get) {
  const counts = new Map();
  for (const c of cards) {
    const v = get(c);
    if (!v) continue;
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  if (counts.size < 2) return [];          // nothing to choose between
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([value, n]) => ({ value, n }));
}

function CarCard({ c, onOpen }) {
  const { eyebrow, name } = nameParts(c.title);
  const boxes = (c.specs || []).filter((s) => isReal(s) && s.label !== 'body').slice(0, 3);
  return (
    <button type="button" className="sr-car" onClick={() => onOpen(c.url)} title={c.title}>
      <div className="sr-car-shot">
        {c.image
          ? (
            <img
              src={c.image} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer"
              onError={(e) => {
                const tile = e.currentTarget.parentElement;
                e.currentTarget.remove();
                if (tile && !tile.querySelector('.sr-car-noshot')) {
                  const s = document.createElement('span');
                  s.className = 'sr-car-noshot';
                  s.textContent = c.host || '';
                  tile.appendChild(s);
                }
              }}
            />
          )
          : <span className="sr-car-noshot">{c.host}</span>}
        <span className="sr-car-fade" aria-hidden="true" />
      </div>
      <div className="sr-car-body">
        {eyebrow && <div className="sr-car-eyebrow">{eyebrow}</div>}
        <div className="sr-car-name">{name}</div>
        <div className="sr-car-price">{c.priceText || c.headline || 'Price not listed'}</div>
        {boxes.length > 0 && (
          <div className="sr-car-boxes">
            {boxes.map((s) => (
              <span key={s.label} className="sr-car-box">
                {/* No unit inside the box: the label under it already says
                    MILES, and "39.4k mi" over the word MILES said it twice
                    and ellipsed for the privilege. */}
                <b>{boxValue(s)}</b>
                <em>{s.label}</em>
              </span>
            ))}
          </div>
        )}
        <div className="sr-car-seller">{c.seller || c.host}</div>
      </div>
    </button>
  );
}

export default function Showroom({ sr, cards, onOpen, compact = false }) {
  // ── THE CARS YOU CAN ACTUALLY LOOK AT COME FIRST (9/3) ─────────────────
  // Measured on two live searches: truecar publishes a photo and a URL per
  // car; iseecars publishes neither — 13 of 24 cards had no image at all and
  // every one of them pointed at the same search page rather than at that
  // car. Nothing is dropped and nothing is invented, but a card with its own
  // photo and its own listing leads, because a screen of grey tiles that all
  // open the same page is not a showroom. Stable within each group, so the
  // seller's own order survives.
  const all = useMemo(() => {
    const list = Array.isArray(cards) ? cards.slice() : [];
    const seen = new Map();
    for (const c of list) seen.set(c.url, (seen.get(c.url) || 0) + 1);
    const rank = (c) => (c.image ? 0 : 1) + (seen.get(c.url) > 1 ? 1 : 0);
    return list
      .map((c, i) => ({ c, i, r: rank(c) }))
      .sort((a, b) => (a.r - b.r) || (a.i - b.i))
      .map((x) => x.c);
  }, [cards]);

  const bounds = useMemo(() => {
    const prices = all.map((c) => c.price).filter((p) => typeof p === 'number' && p > 0);
    const miles = all.map(milesOf).filter((m) => typeof m === 'number' && m > 0);
    return {
      priceLo: prices.length ? Math.min(...prices) : null,
      priceHi: prices.length ? Math.max(...prices) : null,
      milesHi: miles.length ? Math.max(...miles) : null,
    };
  }, [all]);

  const groups = useMemo(() => ([
    { key: 'body', label: 'Body', opts: optionsFor(all, (c) => specValue(c, 'body')) },
    { key: 'drive', label: 'Drivetrain', opts: optionsFor(all, (c) => specValue(c, 'drive')) },
    { key: 'fuel', label: 'Fuel', opts: optionsFor(all, fuelOf) },
    { key: 'color', label: 'Color', opts: optionsFor(all, (c) => baseColour(specValue(c, 'color'))) },
  ].filter((g) => g.opts.length)), [all]);

  const [maxPrice, setMaxPrice] = useState(null);      // null = untouched
  const [maxMiles, setMaxMiles] = useState(null);
  const [picked, setPicked] = useState({});            // { body: Set, drive: Set, ... }

  const toggle = (key, value) => setPicked((prev) => {
    const next = { ...prev };
    const set = new Set(next[key] || []);
    if (set.has(value)) set.delete(value); else set.add(value);
    if (set.size) next[key] = set; else delete next[key];
    return next;
  });

  const getFor = { body: (c) => specValue(c, 'body'), drive: (c) => specValue(c, 'drive'), fuel: fuelOf, color: (c) => baseColour(specValue(c, 'color')) };

  const shown = useMemo(() => all.filter((c) => {
    if (maxPrice != null && typeof c.price === 'number' && c.price > maxPrice) return false;
    if (maxMiles != null) { const m = milesOf(c); if (m != null && m > maxMiles) return false; }
    for (const [key, set] of Object.entries(picked)) {
      const v = getFor[key] ? getFor[key](c) : '';
      if (!set.has(v)) return false;
    }
    return true;
  }), [all, maxPrice, maxMiles, picked]);

  const touched = maxPrice != null || maxMiles != null || Object.keys(picked).length > 0;
  const reset = () => { setMaxPrice(null); setMaxMiles(null); setPicked({}); };

  const shownPrices = shown.map((c) => c.price).filter((p) => typeof p === 'number' && p > 0);
  const lo = shownPrices.length ? Math.min(...shownPrices) : null;
  const hi = shownPrices.length ? Math.max(...shownPrices) : null;

  const where = sr.area || sr.onSite || '';

  return (
    <div className={'showroom' + (compact ? ' showroom--compact' : '')}>
      <div className="sr-glow" aria-hidden="true" />
      <div className="sr-panel">
        <header className="sr-head">
          <div className="sr-brand">{sr.asked || sr.query || 'Cars'}</div>
          <div className="sr-head-right">
            <span className="sr-tag">{all.length} cars</span>
            {where && <span className="sr-tag">{where}</span>}
            {lo != null && hi != null && hi > lo && <span className="sr-tag sr-tag--price">{money(lo)}–{money(hi)}</span>}
          </div>
        </header>

        <div className="sr-body">
          <aside className="sr-filters">
            <div className="sr-filters-top">
              <div className="sr-filters-title">Buyer filters</div>
              {touched && <button type="button" className="sr-reset" onClick={reset}>Reset</button>}
            </div>

            {bounds.priceLo != null && bounds.priceHi != null && bounds.priceHi > bounds.priceLo && (
              <div className="sr-group">
                <div className="sr-group-label">Price up to</div>
                <div className="sr-readout">{money(maxPrice == null ? bounds.priceHi : maxPrice)}</div>
                <input
                  className="sr-slider" type="range"
                  min={Math.floor(bounds.priceLo)} max={Math.ceil(bounds.priceHi)}
                  step={Math.max(100, Math.round((bounds.priceHi - bounds.priceLo) / 60))}
                  value={maxPrice == null ? Math.ceil(bounds.priceHi) : maxPrice}
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                  aria-label="Maximum price"
                />
              </div>
            )}

            {bounds.milesHi != null && (
              <div className="sr-group">
                <div className="sr-group-label">Miles up to</div>
                <div className="sr-readout">{(maxMiles == null ? bounds.milesHi : maxMiles).toLocaleString('en-US')} mi</div>
                <input
                  className="sr-slider" type="range"
                  min={0} max={Math.ceil(bounds.milesHi)}
                  step={Math.max(500, Math.round(bounds.milesHi / 60))}
                  value={maxMiles == null ? Math.ceil(bounds.milesHi) : maxMiles}
                  onChange={(e) => setMaxMiles(Number(e.target.value))}
                  aria-label="Maximum miles"
                />
              </div>
            )}

            {groups.map((g) => (
              <div className="sr-group" key={g.key}>
                <div className="sr-group-label">{g.label}</div>
                <div className="sr-opts">
                  {g.opts.map((o) => {
                    const on = !!(picked[g.key] && picked[g.key].has(o.value));
                    return (
                      <button
                        key={o.value} type="button"
                        className={'sr-opt' + (on ? ' is-on' : '')}
                        aria-pressed={on}
                        onClick={() => toggle(g.key, o.value)}
                      >
                        <span className="sr-opt-box" aria-hidden="true" />
                        <span className="sr-opt-text">{o.value}</span>
                        <span className="sr-opt-n">{o.n}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </aside>

          <div className="sr-lot">
            {shown.length > 0 ? (
              <div className="sr-row">
                {shown.map((c, i) => <CarCard key={(c.url || '') + i} c={c} onOpen={onOpen} />)}
              </div>
            ) : (
              <div className="sr-empty">
                Nothing left at these filters.
                <button type="button" className="sr-reset" onClick={reset}>Reset</button>
              </div>
            )}
          </div>
        </div>

        <footer className="sr-foot">
          <span>{touched ? `${shown.length} of ${all.length} shown` : `${all.length} on the lot`}</span>
          {sr.provenance && <span className="sr-prov">{sr.provenance}</span>}
        </footer>
      </div>
      <div className="sr-reflection" aria-hidden="true" />
    </div>
  );
}
